import { Dispatch, SetStateAction, useState } from "react";
import {
  DragHandle,
  PopupSize,
  PopupTemplate,
  PopupWarningDisplayStrategyForbiddenOperation,
} from "..";
import { useSelectTheme } from "../../store";
import { QuestionnaireTempSection, TempQuestionnaireDisplayStrategyUpsertItem } from "../../model";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ChevronDownIcon } from "../../lib/svg/icon/chevron_down";
import { closestCenter, DndContext, DragEndEvent, PointerSensor, useSensor } from "@dnd-kit/core";
import {
  IQuestionnaireDisplayRuleTargetQuestion,
  IQuestionnaireDisplayRuleTargetSection,
  IQuestionnaireDisplayRuleTargetSubmit,
  IQuestionnaireDisplayRuleTargetType,
} from "../../shared/db";

export const PopupReorderQuestionnaireSection = (props: {
  setRenderPopupReorderSection: Dispatch<SetStateAction<boolean>>;
  tempSectionList: QuestionnaireTempSection[];
  setTempSectionList: Dispatch<SetStateAction<QuestionnaireTempSection[]>>;
  upsertDisplayStrategyList: TempQuestionnaireDisplayStrategyUpsertItem[];
}) => {
  const { color, fontSize, fontWeight } = useSelectTheme();
  const {
    setRenderPopupReorderSection,
    tempSectionList,
    setTempSectionList,
    upsertDisplayStrategyList,
  } = props;

  const [sectionList, setSectionList] = useState<QuestionnaireTempSection[]>(tempSectionList);
  const [
    renderPopupWarningDisplayStrategyForbiddenOperation,
    setRenderPopupWarningDisplayStrategyForbiddenOperation,
  ] = useState(false);
  const handleClosePopupWarningDisplayStrategyForbiddenOperation = () => {
    setRenderPopupWarningDisplayStrategyForbiddenOperation(false);
  };

  const lockedByDisplayStrategy = (index: number) => {
    const section = sectionList[index];
    const currentDisplayStrategy = upsertDisplayStrategyList.find(
      (item) => item.sectionId === section.id,
    );
    if (section.questionList?.some(({ id }) => isQuestionLockedByDisplayStrategy(id))) {
      return true;
    }
    if (!currentDisplayStrategy) return false;
    if (
      currentDisplayStrategy.displayStrategy.displayLogicList?.some(({ displayRuleList }) => {
        if (displayRuleList && displayRuleList.length > 0) return true;
      })
    ) {
      return true;
    }
    return upsertDisplayStrategyList.some(({ displayStrategy }) => {
      return displayStrategy.displayLogicList?.some(({ displayRuleList }) =>
        displayRuleList?.some((rule) => {
          const target = rule.target as
            | IQuestionnaireDisplayRuleTargetSection
            | IQuestionnaireDisplayRuleTargetQuestion
            | IQuestionnaireDisplayRuleTargetSubmit;
          if (!target) return false;
          return (
            target.type === IQuestionnaireDisplayRuleTargetType.Section &&
            target.questionnaireSectionId === section.id
          );
        }),
      );
    });
  };

  const isQuestionLockedByDisplayStrategy = (qid: string) => {
    if (!upsertDisplayStrategyList.some(({ questionId }) => questionId === qid)) return false;
    return upsertDisplayStrategyList
      .filter(({ questionId }) => questionId === qid)
      .some(({ displayStrategy }) => {
        return displayStrategy.displayLogicList?.some(({ displayRuleList }) => {
          if (!displayRuleList) return false;
          return displayRuleList.length > 0;
        });
      });
  };

  const moveSection = (fromIndex: number, toIndex: number) => {
    if (lockedByDisplayStrategy(fromIndex))
      return setRenderPopupWarningDisplayStrategyForbiddenOperation(true);
    if (toIndex < 0 || toIndex >= sectionList.length) return;
    const newTempSectionList = arrayMove([...sectionList], fromIndex, toIndex).map((s, index) => ({
      ...s,
      index,
    }));
    setSectionList(newTempSectionList);
  };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over) return;
    if (active.id === over.id) return;
    const fromIndex = sectionList.findIndex(({ id }) => id === active.id);
    const toIndex = sectionList.findIndex(({ id }) => id === over.id);
    if (fromIndex === -1 || toIndex === -1) return;
    moveSection(fromIndex, toIndex);
  };

  const moveUp = (index: number) => {
    moveSection(index, index - 1);
  };

  const moveDown = (index: number) => {
    moveSection(index, index + 1);
  };

  const handleSaveOrder = () => {
    setTempSectionList(sectionList.map((s, i) => ({ ...s, index: i })));
    setRenderPopupReorderSection(false);
  };

  const SectionListItem = (props: { section: QuestionnaireTempSection; index: number }) => {
    const { section, index } = props;
    const { attributes, listeners, setNodeRef, transform, setActivatorNodeRef } = useSortable({
      id: section.id,
    });
    const style = { transform: CSS.Translate.toString(transform) };
    const isLast = index === sectionList.length - 1;

    return (
      <div
        ref={setNodeRef}
        {...attributes}
        css={{
          ...style,
          width: "100%",
          padding: "16px 24px",
          borderTop: index === 0 ? `1px solid ${color.border.light}` : "none",
          borderBottom: `1px solid ${color.border.light}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {/* left part */}
        <div css={{ display: "flex", gap: "24px", alignItems: "center" }}>
          {/* drag handle */}
          <DragHandle ref={setActivatorNodeRef} {...listeners} />
          {/* section info */}
          <div
            css={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
            }}
          >
            <div
              css={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <span css={{ fontWeight: fontWeight.bold }}>
                {section.legend.length > 0 ? section.legend : "Untitled Section"}
              </span>
              <span css={{ fontWeight: fontWeight.bold, color: color.textIcon.description }}>{`- ${
                section.questionList?.length
              } question${section.questionList?.length !== 1 ? "s" : ""}`}</span>
            </div>
            <span
              css={{ fontWeight: fontWeight.bold, color: color.textIcon.description }}
            >{`Section ${index + 1} of ${sectionList.length}`}</span>
          </div>
        </div>
        {/* right part: up/down button */}
        <div css={{ display: "flex", gap: "16px" }}>
          {index > 0 && (
            <button
              onClick={() => moveUp(index)}
              css={{
                width: "32px",
                height: "32px",
                borderRadius: "4px",
                border: `1px solid ${color.border.default}`,
                backgroundColor: color.surface.white,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transform: "rotate(180deg)",
                ":hover": {
                  backgroundColor: color.surface.hover.hoverWhiteBg,
                },
              }}
            >
              <ChevronDownIcon {...{ width: 20, height: 20, fill: color.textIcon.greyIcon }} />
            </button>
          )}
          {!isLast && (
            <button
              onClick={() => moveDown(index)}
              css={{
                width: "32px",
                height: "32px",
                borderRadius: "4px",
                border: `1px solid ${color.border.default}`,
                backgroundColor: color.surface.white,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                ":hover": {
                  backgroundColor: color.surface.hover.hoverWhiteBg,
                },
              }}
            >
              <ChevronDownIcon {...{ width: 20, height: 20, fill: color.textIcon.greyIcon }} />
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      {renderPopupWarningDisplayStrategyForbiddenOperation && (
        <PopupWarningDisplayStrategyForbiddenOperation
          {...{ handleClosePopup: handleClosePopupWarningDisplayStrategyForbiddenOperation }}
        />
      )}
      <PopupTemplate
        {...{
          title: "Reorder Section",
          description:
            "Changing the order of sections will update how questions are shown to participants. It may also affect any logic connected to this section.",
          background: true,
          popupSize: PopupSize.Medium,
          handleClosePopup: () => setRenderPopupReorderSection(false),
          mainFunction: handleSaveOrder,
          buttonContent: "Save",
        }}
      >
        <DndContext
          sensors={[useSensor(PointerSensor)]}
          collisionDetection={closestCenter}
          onDragEnd={onDragEnd}
        >
          <SortableContext items={sectionList} strategy={verticalListSortingStrategy}>
            {sectionList.map((section, index) => (
              <SectionListItem
                {...{
                  key: section.id,
                  section,
                  index,
                }}
              />
            ))}
          </SortableContext>
        </DndContext>
      </PopupTemplate>
    </>
  );
};
